/** Small pictures for readings: tank fill, wind direction, health ring, a bar for soil moisture. */
import { compass } from "./format.ts";

export function Tank({ percent, label }: { percent: number; label?: string }) {
  return (
    <div className="tank-vis" role="img" aria-label={`${label ?? "Tank"} ${percent}% full`}>
      <div className="tank-fill" style={{ height: `${percent}%` }} />
      <div className="tank-pct">{percent}%</div>
    </div>
  );
}

/** Arrow points where the wind is blowing FROM, like a weather map. */
export function WindDial({ degrees, size = 84 }: { degrees: number; size?: number }) {
  const r = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`Wind from ${compass(degrees)}`}>
      <circle cx={r} cy={r} r={r - 2} fill="var(--surface-2)" stroke="var(--line)" />
      {["N", "E", "S", "W"].map((d, i) => {
        const a = (i * Math.PI) / 2;
        return (
          <text key={d} x={r + Math.sin(a) * (r - 11)} y={r - Math.cos(a) * (r - 11) + 4} textAnchor="middle" fontSize="10" fill="var(--muted)" fontWeight="700">
            {d}
          </text>
        );
      })}
      <g transform={`rotate(${degrees} ${r} ${r})`}>
        <path d={`M${r} ${r - (r - 18)} L${r - 7} ${r + 8} L${r} ${r + 3} L${r + 7} ${r + 8} Z`} fill="var(--water)" transform={`rotate(180 ${r} ${r})`} />
      </g>
    </svg>
  );
}

export function Ring({ value, size = 96 }: { value: number; size?: number }) {
  const r = size / 2 - 7;
  const c = 2 * Math.PI * r;
  const tone = value >= 85 ? "var(--good)" : value >= 60 ? "var(--warn)" : "var(--crit)";
  return (
    <div className="ring" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--line)" strokeWidth="8" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={tone} strokeWidth="8" strokeLinecap="round" strokeDasharray={`${(c * value) / 100} ${c}`} />
      </svg>
      <div className="ring-val">
        <b>{value}</b>
        <span>Health</span>
      </div>
    </div>
  );
}

/** Soil moisture against a refill point and full point, when known. */
export function MoistureBar({ value, refill = 25, full = 40 }: { value: number; refill?: number; full?: number }) {
  const max = Math.max(full * 1.25, value * 1.1, 1);
  const pct = (v: number) => `${Math.min(100, (100 * v) / max)}%`;
  const tone = value < refill ? "var(--crit)" : value > full ? "var(--water)" : "var(--good)";
  return (
    <div className="moist" aria-hidden="true">
      <div className="moist-band" style={{ left: pct(refill), width: `calc(${pct(full)} - ${pct(refill)})` }} />
      <div className="moist-fill" style={{ width: pct(value), background: tone }} />
    </div>
  );
}

/** A half-dial: the needle and filled arc show where the value sits in its range. */
export function Gauge({ value, min, max, label }: { value: number; min: number; max: number; label: string }) {
  const f = Math.max(0, Math.min(1, (value - min) / (max - min || 1)));
  const W = 140;
  const r = 56;
  const cx = W / 2;
  const cy = 66;
  const at = (t: number) => [cx - r * Math.cos(Math.PI * t), cy - r * Math.sin(Math.PI * t)] as const;
  const [ex, ey] = at(f);
  const [nx, ny] = [cx - (r - 14) * Math.cos(Math.PI * f), cy - (r - 14) * Math.sin(Math.PI * f)];
  return (
    <svg className="wsvg" viewBox={`0 0 ${W} 84`} role="img" aria-label={`${label}: ${Math.round(f * 100)} % of its range`}>
      <path d={`M${cx - r} ${cy} A${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="var(--surface-2)" strokeWidth="12" strokeLinecap="round" />
      {f > 0.005 && <path d={`M${cx - r} ${cy} A${r} ${r} 0 0 1 ${ex.toFixed(1)} ${ey.toFixed(1)}`} fill="none" stroke="var(--water)" strokeWidth="12" strokeLinecap="round" />}
      <line x1={cx} y1={cy} x2={nx.toFixed(1)} y2={ny.toFixed(1)} stroke="var(--ink)" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="4.5" fill="var(--ink)" />
      <text x={cx - r} y={cy + 16} textAnchor="middle" fontSize="10" fill="var(--muted)">
        {fmtShort(min)}
      </text>
      <text x={cx + r} y={cy + 16} textAnchor="middle" fontSize="10" fill="var(--muted)">
        {fmtShort(max)}
      </text>
    </svg>
  );
}

/** A thermometer filled to the value within its range; the bulb turns warm above 35 °C, cold below 2 °C. */
export function Thermometer({ value, min, max, label }: { value: number; min: number; max: number; label: string }) {
  const f = Math.max(0, Math.min(1, (value - min) / (max - min || 1)));
  const tone = value >= 35 ? "var(--crit)" : value <= 2 ? "var(--water)" : "var(--orange)";
  const top = 8;
  const bottom = 70;
  const y = bottom - f * (bottom - top);
  return (
    <svg className="wsvg" viewBox="0 0 60 96" style={{ maxHeight: 96 }} role="img" aria-label={`${label}: thermometer`}>
      <rect x="23" y="4" width="14" height="72" rx="7" fill="var(--surface-2)" stroke="var(--line)" />
      <rect x="27" y={y} width="6" height={bottom - y + 8} fill={tone} />
      <circle cx="30" cy="82" r="11" fill={tone} stroke="var(--line)" />
      {[0, 0.5, 1].map((t) => (
        <g key={t}>
          <line x1="39" x2="44" y1={bottom - t * (bottom - top)} y2={bottom - t * (bottom - top)} stroke="var(--muted)" />
          <text x="46" y={bottom - t * (bottom - top) + 3} fontSize="8.5" fill="var(--muted)">
            {fmtShort(min + t * (max - min))}
          </text>
        </g>
      ))}
    </svg>
  );
}

/** The last day as a small line, newest on the right. */
export function Sparkline({ values, label }: { values: number[]; label: string }) {
  if (values.length < 2) return <div className="muted small">Not enough history yet</div>;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const W = 160;
  const H = 46;
  const pts = values.map((v, i) => `${((i / (values.length - 1)) * W).toFixed(1)},${(H - 4 - ((v - lo) / (hi - lo || 1)) * (H - 8)).toFixed(1)}`);
  return (
    <svg className="wsvg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ height: H }} role="img" aria-label={`${label}: last 24 hours`}>
      <path d={`M0,${H} L${pts.join(" L")} L${W},${H} Z`} fill="var(--water)" opacity=".12" />
      <path d={`M${pts.join(" L")}`} fill="none" stroke="var(--water)" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
    </svg>
  );
}

function fmtShort(v: number): string {
  const a = Math.abs(v);
  return a >= 1000 ? `${Math.round(v / 100) / 10}k` : a >= 10 ? String(Math.round(v)) : String(Math.round(v * 10) / 10);
}

/**
 * Wind rose: how often the wind came from each of 16 directions (petal length) and how hard
 * (petal colour), from the last day's readings, with the current direction and speed in the
 * middle. Needs matching direction and speed histories.
 */
export function WindRose({ dirs, speeds, nowDir, nowSpeed, unit = "km/h", size = 160 }: { dirs: number[]; speeds: number[]; nowDir?: number; nowSpeed?: number; unit?: string; size?: number }) {
  const n = 16;
  const bins = Array.from({ length: n }, () => ({ count: 0, speed: 0 }));
  dirs.forEach((d, i) => {
    const b = Math.round((((d % 360) + 360) % 360) / (360 / n)) % n;
    bins[b]!.count++;
    bins[b]!.speed += speeds[i] ?? 0;
  });
  const max = Math.max(1, ...bins.map((b) => b.count));
  const maxSpeed = Math.max(1, ...bins.map((b) => (b.count ? b.speed / b.count : 0)));
  const c = size / 2;
  const R = c - 14;
  const petal = (i: number, r: number) => {
    const a0 = ((i - 0.5) * 2 * Math.PI) / n - Math.PI / 2;
    const a1 = ((i + 0.5) * 2 * Math.PI) / n - Math.PI / 2;
    return `M${c},${c} L${(c + r * Math.cos(a0)).toFixed(1)},${(c + r * Math.sin(a0)).toFixed(1)} A${r},${r} 0 0 1 ${(c + r * Math.cos(a1)).toFixed(1)},${(c + r * Math.sin(a1)).toFixed(1)} Z`;
  };
  return (
    <svg className="wsvg rose" viewBox={`0 0 ${size} ${size}`} style={{ maxHeight: size }} role="img" aria-label={`Wind rose: ${dirs.length} readings`}>
      {[0.33, 0.66, 1].map((f) => (
        <circle key={f} cx={c} cy={c} r={R * f} fill="none" stroke="var(--line)" />
      ))}
      {bins.map((b, i) =>
        b.count ? <path key={i} d={petal(i, 6 + (R - 6) * (b.count / max))} fill="var(--water)" opacity={0.35 + 0.6 * (b.speed / b.count / maxSpeed)} stroke="var(--surface)" strokeWidth="1" /> : null,
      )}
      {["N", "E", "S", "W"].map((d, i) => {
        const a = (i * Math.PI) / 2 - Math.PI / 2;
        return (
          <text key={d} x={c + (R + 9) * Math.cos(a)} y={c + (R + 9) * Math.sin(a) + 3.5} textAnchor="middle" fontSize="10" fontWeight="700" fill="var(--muted)">
            {d}
          </text>
        );
      })}
      {nowDir !== undefined && (
        <g transform={`rotate(${nowDir} ${c} ${c})`}>
          <path d={`M${c} ${c - R * 0.55} L${c - 5} ${c - R * 0.3} L${c} ${c - R * 0.36} L${c + 5} ${c - R * 0.3} Z`} fill="var(--ink)" transform={`rotate(180 ${c} ${c})`} />
        </g>
      )}
      <circle cx={c} cy={c} r={size * 0.17} fill="var(--surface)" stroke="var(--line)" />
      {nowSpeed !== undefined && (
        <>
          <text x={c} y={c + 2} textAnchor="middle" fontSize={size * 0.11} fontWeight="700" fill="var(--ink)" fontFamily="var(--f-display)">
            {Math.round(nowSpeed)}
          </text>
          <text x={c} y={c + size * 0.09} textAnchor="middle" fontSize="8.5" fill="var(--muted)">
            {unit}
          </text>
        </>
      )}
    </svg>
  );
}

/**
 * Soil profile: moisture at each probe depth as a horizontal bar down the soil, against the
 * refill and full points, so it's clear where the water is and where it's needed.
 */
export function SoilProfile({ layers, refill = 25, full = 40, unit = "%" }: { layers: Array<{ depth: string; value: number }>; refill?: number; full?: number; unit?: string }) {
  const max = Math.max(full * 1.3, ...layers.map((l) => l.value * 1.1), 1);
  const x = (v: number) => (100 * Math.min(v, max)) / max;
  const tone = (v: number) => (v < refill ? "var(--crit)" : v > full ? "var(--water)" : "var(--good)");
  return (
    <div className="soil-profile" role="img" aria-label={`Soil moisture at ${layers.length} depths`}>
      <div className="soil-band" style={{ left: `calc(54px + (100% - 54px - 58px) * ${x(refill) / 100})`, width: `calc((100% - 54px - 58px) * ${(x(full) - x(refill)) / 100})` }} />
      {layers.map((l) => (
        <div key={l.depth} className="soil-layer">
          <span className="soil-depth">{l.depth}</span>
          <span className="soil-bar">
            <span style={{ width: `${x(l.value)}%`, background: tone(l.value) }} />
          </span>
          <span className="soil-val num">
            {Math.round(l.value * 10) / 10}
            {unit}
          </span>
        </div>
      ))}
      <div className="soil-legend">
        <span>Refill {refill}{unit}</span>
        <span>Full {full}{unit}</span>
      </div>
    </div>
  );
}

/** Flow meter: today's total as the big number, with the rate it's running at now. */
export function FlowMeter({ today, rate, unit = "kL", rateUnit = "L/s" }: { today: number; rate?: number; unit?: string; rateUnit?: string }) {
  const spinning = rate !== undefined && rate > 0;
  return (
    <div className="flow-meter" role="img" aria-label={`Flow meter, ${Math.round(today)} ${unit} today`}>
      <svg viewBox="0 0 48 48" width="44" height="44" aria-hidden="true">
        <circle cx="24" cy="24" r="20" fill="var(--water-soft)" stroke="var(--water)" strokeWidth="3" />
        <g className={spinning ? "spin" : ""} style={{ transformOrigin: "24px 24px" }}>
          {[0, 90, 180, 270].map((a) => (
            <path key={a} d="M24 24 L24 8 Q30 12 24 20 Z" fill="var(--water)" transform={`rotate(${a} 24 24)`} />
          ))}
        </g>
        <circle cx="24" cy="24" r="3" fill="var(--ink)" />
      </svg>
      <div>
        <div className="flow-rate">{rate !== undefined ? `${Math.round(rate * 10) / 10} ${rateUnit}` : "—"}</div>
        <div className="muted small">{rate !== undefined ? (spinning ? "flowing now" : "no flow now") : "rate unknown"}</div>
      </div>
    </div>
  );
}

/** A green or red light for an on/off input (a float switch, a VSD run signal, a trip). */
export function StatusLight({ on, label, onText = "On", offText = "Off", bad }: { on: boolean | undefined; label: string; onText?: string; offText?: string; bad?: boolean }) {
  const tone = on === undefined ? "unknown" : (on ? "on" : "off") + (bad ? " bad" : "");
  return (
    <span className={`light ${tone}`} role="img" aria-label={`${label}: ${on === undefined ? "unknown" : on ? onText : offText}`}>
      <i />
      {on === undefined ? "Unknown" : on ? onText : offText}
    </span>
  );
}

/** A big switch for an output; the dot only moves when the device reports the new state. */
export function Toggle({ on, pending, disabled, label, onChange }: { on: boolean | undefined; pending?: boolean; disabled?: boolean; label: string; onChange: (on: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on === true}
      aria-label={label}
      className={`toggle ${on === true ? "on" : ""} ${pending ? "pending" : ""}`}
      disabled={disabled}
      onClick={() => onChange(!(on === true))}
    >
      <span className="knob" />
      <span className="toggle-text">{pending ? "…" : on === undefined ? "?" : on ? "ON" : "OFF"}</span>
    </button>
  );
}

/** Line colours for soil depths: shallow to deep. */
export const SOIL_COLOURS = ["var(--c4, #c2662b)", "var(--water)", "var(--c3, #7b5bc2)", "var(--good)", "var(--warn)"];

/**
 * Soil moisture over the last days, as in the prototype: one line per depth, the band between
 * the refill and full points shaded, dashed lines on each, and today's value at the end of
 * each line. The band is where the crop's roots have the water they need.
 */
export function SoilChart({
  series,
  refill,
  full,
  from,
  to,
  unit = "%",
  height = 220,
}: {
  series: Array<{ label: string; points: Array<{ at: number; value: number }> }>;
  refill: number;
  full: number;
  from: number;
  to: number;
  unit?: string;
  height?: number;
}) {
  const W = 640;
  const H = height;
  const L = 40;
  const R = 96;
  const T = 12;
  const B = 24;
  const iw = W - L - R;
  const ih = H - T - B;
  const all = series.flatMap((s) => s.points.map((p) => p.value));
  const lo = Math.floor(Math.min(refill, ...(all.length ? all : [refill])) / 5) * 5 - 2;
  const hi = Math.ceil(Math.max(full, ...(all.length ? all : [full])) / 5) * 5 + 2;
  const x = (t: number) => L + ((t - from) / (to - from || 1)) * iw;
  const y = (v: number) => T + ih - ((v - lo) / (hi - lo || 1)) * ih;
  const days = Math.round((to - from) / 86_400_000);
  const ticks = Array.from({ length: Math.min(days, 7) + 1 }, (_, i) => from + ((to - from) * i) / Math.min(days, 7));
  const yTicks = [lo + 2, Math.round((lo + hi) / 2), hi - 2];
  const label = (t: number) => new Date(t).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  // End labels nudged apart so two depths at the same value don't print over each other.
  const ends = series
    .map((s, j) => ({ j, last: s.points[s.points.length - 1] }))
    .filter((e): e is { j: number; last: { at: number; value: number } } => !!e.last)
    .sort((a, b) => y(a.last.value) - y(b.last.value));
  let prev = -Infinity;
  const endY = new Map<number, number>();
  for (const e of ends) {
    const yy = Math.max(y(e.last.value) + 4, prev + 14);
    endY.set(e.j, yy);
    prev = yy;
  }
  return (
    <svg className="soil-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Soil moisture by depth, last ${days} days`}>
      <rect x={L} y={y(full)} width={iw} height={Math.max(0, y(refill) - y(full))} fill="var(--good-soft)" />
      {yTicks.map((v) => (
        <g key={v}>
          <line x1={L} x2={L + iw} y1={y(v)} y2={y(v)} stroke="var(--line)" strokeWidth="1" />
          <text x={L - 6} y={y(v) + 4} fontSize="11" textAnchor="end" fill="var(--muted)">
            {v}
          </text>
        </g>
      ))}
      {ticks.map((t, i) => (
        <text key={i} x={x(t)} y={H - 6} fontSize="11" textAnchor={i === 0 ? "start" : i === ticks.length - 1 ? "end" : "middle"} fill="var(--muted)">
          {label(t)}
        </text>
      ))}
      <line x1={L} x2={L + iw} y1={y(full)} y2={y(full)} stroke="var(--good)" strokeDasharray="5 4" />
      <text x={L + 6} y={y(full) - 5} fontSize="11.5" fill="var(--good)" className="halo">
        Full point {full}
        {unit}
      </text>
      <line x1={L} x2={L + iw} y1={y(refill)} y2={y(refill)} stroke="var(--warn)" strokeDasharray="5 4" />
      <text x={L + 6} y={y(refill) + 15} fontSize="11.5" fill="var(--warn)" className="halo">
        Refill point {refill}
        {unit}
      </text>
      {series.map((s, j) => {
        const pts = s.points.filter((p) => p.at >= from && p.at <= to);
        if (!pts.length) return null;
        const last = pts[pts.length - 1]!;
        const colour = SOIL_COLOURS[j % SOIL_COLOURS.length];
        return (
          <g key={s.label}>
            <polyline points={pts.map((p) => `${x(p.at).toFixed(1)},${y(p.value).toFixed(1)}`).join(" ")} fill="none" stroke={colour} strokeWidth="2.2" strokeLinejoin="round" />
            <circle cx={x(last.at)} cy={y(last.value)} r="4" fill={colour} stroke="var(--surface)" strokeWidth="2" />
            <text x={x(last.at) + 9} y={endY.get(j) ?? y(last.value) + 4} fontSize="12" fontWeight="600" fill={colour}>
              {s.label} {Math.round(last.value)}
              {unit}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
